import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Zap, ChevronRight, Flame } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import PageTransition from '../components/layout/PageTransition';
import ScrollReveal from '../components/layout/ScrollReveal';
import { getProject } from '../lib/api';
import CaseStudy from '../components/CaseStudy';
import { CASE_STUDIES } from '../lib/caseStudies';

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const study = CASE_STUDIES[slug];

  useEffect(() => {
    setLoading(true);
    setError(null);
    getProject(slug)
      .then((res) => {
        setProject(res.data.project);
        document.title = res.data.project.name;
      })
      .catch(() => setError('Project not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <PageTransition>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <Skeleton className="h-4 w-24 mb-8 bg-white/10" />
          <Skeleton className="h-10 w-64 mb-4 bg-white/10" />
          <Skeleton className="h-5 w-20 mb-8 bg-white/10" />
          <Skeleton className="h-40 w-full bg-white/10" />
        </div>
      </PageTransition>
    );
  }

  if (error || !project) {
    return (
      <PageTransition>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 text-center">
          <h1 className="text-3xl font-semibold mb-4">Project Not Found</h1>
          <p className="text-white/55 mb-6">The project you're looking for doesn't exist or is currently hidden.</p>
          <Link to="/ecosystem">
            <Button variant="outline" className="bg-white/5 border-white/10">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Ecosystem
            </Button>
          </Link>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <section className="relative pt-24 sm:pt-28 lg:pt-36 pb-14 sm:pb-20">
        <div className="absolute inset-0 bg-fire-engine" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal hero>
            <Link to="/ecosystem" className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-white/80 mb-6 transition-colors">
              <ArrowLeft className="h-3 w-3" />
              Back to Ecosystem
            </Link>

            <div className="flex items-center gap-4 mb-4">
              {project.logoUrl && (
                <img src={project.logoUrl} alt={project.name} className="h-12 w-12 rounded-lg object-contain" />
              )}
              <div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[0.95]" data-testid="project-name">
                  {project.name}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-2">
              <StatusBadge status={project.status} />
              {project.category && (
                <span className="text-xs text-white/55 font-mono">{project.category}</span>
              )}
            </div>

            <p className="text-base md:text-lg text-white/72 leading-relaxed max-w-2xl mt-4">
              {project.tagline}
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 relative">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-10">
            <div>
              <ScrollReveal>
                <span className="kicker">Overview</span>
                <p className="text-sm md:text-base text-white/72 leading-relaxed mt-3 mb-8">
                  {project.description}
                </p>
              </ScrollReveal>

              {project.features && project.features.length > 0 && (
                <ScrollReveal delay={0.1}>
                  <span className="kicker">Capabilities</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    {project.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 px-4 py-3 rounded-[4px] border border-white/8 bg-white/[0.02]">
                        <Zap className="h-3.5 w-3.5 text-[#E8722A] flex-shrink-0" />
                        <span className="text-sm text-white/70">{f}</span>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>
              )}

              <ScrollReveal delay={0.2}>
                <div className="flex flex-wrap gap-3 mt-8">
                  {project.websiteUrl && (
                    <a href={project.websiteUrl} target="_blank" rel="noopener noreferrer">
                      <Button data-testid="project-website-link" className="btn-primary-gradient btn-shimmer font-semibold rounded-lg">
                        Visit Website
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  )}
                  {project.guideUrl && (
                    <a href={project.guideUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="bg-white/5 text-white/85 border-white/10 hover:bg-white/8">
                        View Guide
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  )}
                </div>
              </ScrollReveal>
            </div>

            <div className="lg:sticky lg:top-24 lg:self-start">
              <ScrollReveal delay={0.2} direction="right" distance={20}>
                <div className="hud-card border border-white/10 bg-white/[0.03] p-6">
                  <span className="kicker block mb-4">Project Details</span>
                  <div className="space-y-4">
                    <MetaRow label="Status" value={project.status} accent={project.status === 'Live'} />
                    <MetaRow label="Category" value={project.category || 'Platform'} />
                    {project.websiteUrl && (
                      <MetaRow label="Website" value={project.websiteUrl.replace(/^https?:\/\//, '')} link={project.websiteUrl} />
                    )}
                    <MetaRow label="Infrastructure" value="Visualte Engine" accent />
                  </div>

                  <Separator className="my-5 bg-white/8" />

                  <div className="flex items-center gap-2">
                    <Flame className="h-3.5 w-3.5 text-[#E8722A]" />
                    <span className="text-xs text-white/40">Powered by Visualte</span>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <CaseStudy study={study} projectName={project.name} />
    </PageTransition>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Live: 'bg-[rgba(232,114,42,0.14)] text-[rgba(255,180,80,0.95)] border-[rgba(232,114,42,0.25)]',
    Beta: 'bg-[rgba(255,180,0,0.12)] text-[rgba(255,200,80,0.92)] border-[rgba(255,180,0,0.22)]',
    'Coming Soon': 'bg-white/8 text-white/70 border-white/12',
  };
  return (
    <Badge className={`${styles[status] || styles['Coming Soon']} text-[10px] font-mono uppercase`}>
      {status === 'Live' && <span className="w-1.5 h-1.5 rounded-full bg-[#E8722A] mr-1.5 status-pulse inline-block" />}
      {status}
    </Badge>
  );
}

function MetaRow({ label, value, accent, link }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-white/6 last:border-0">
      <span className="text-xs text-white/55">{label}</span>
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-sm font-mono text-[#E8722A] hover:underline">
          {value}
        </a>
      ) : (
        <span className={`text-sm font-mono ${accent ? 'text-[#E8722A]' : 'text-white/80'}`}>{value}</span>
      )}
    </div>
  );
}
